import React, { useEffect, useState } from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { Bar, Line, Doughnut, Radar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  RadialLinearScale,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { useNavigate } from 'react-router-dom';
import API from '../services/api';
import { monthLabel } from '../utils/dates';
import Loading from './Loading';
import { equipmentKind } from '../utils/equipmentLabel';
import DailyBrief from './DailyBrief';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, RadialLinearScale, Filler, Tooltip, Legend);

const money = (n) => new Intl.NumberFormat('es-DO', { style: 'currency', currency: 'DOP' }).format(n || 0);
const MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const MONTH_NAMES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const PERIOD_ROWS = [['Hoy', 'day'], ['Esta semana', 'week'], ['Este mes', 'month']];
const PERIOD_ROWS_YEAR = [...PERIOD_ROWS, ['Este año', 'year']];
const HEADING_FONT = "'DM Sans', sans-serif";

// Today / this week / this month (and, for cards that pass PERIOD_ROWS_YEAR, this year too) for one figure, "hoy"
// biggest. `values` = { day, week, month, year? }.
const PeriodRows = ({ values, format, color, rows = PERIOD_ROWS }) => (
  <div className="mt-2">
    {rows.map(([label, key], i) => (
      <div key={key} className="d-flex justify-content-between align-items-baseline" style={{ marginTop: i ? 4 : 0 }}>
        <span className="text-muted small">{label}</span>
        <span style={{ fontFamily: HEADING_FONT, fontWeight: i === 0 ? 600 : 500, fontSize: i === 0 ? '1.35rem' : '1.05rem', color: i === 0 ? color : undefined }}>
          {format(values?.[key])}
        </span>
      </div>
    ))}
  </div>
);

// This calendar month vs. the same month last year, stock-ticker style (an up/down caret + percentage, colored
// green/red). Spells out which two figures are being compared — "Este mes" ties it back to the row of the same
// name above, and both amounts are shown on their own line — since a bare "Comparado con <mes>" plus a lone
// percentage left it unclear what was actually being measured against what.
const YoyComparison = ({ current, previous, month, year, format = String }) => {
  const label = `${MONTH_NAMES[((month || 1) - 1 + 12) % 12]} ${year}`;
  const hasPrevious = previous > 0;
  const pct = hasPrevious ? ((current - previous) / previous) * 100 : null;
  const isUp = pct !== null && pct > 0.05;
  const isDown = pct !== null && pct < -0.05;
  const color = isUp ? 'var(--mm-green)' : isDown ? 'var(--mm-red)' : 'var(--mm-muted)';
  const icon = isUp ? 'bi-caret-up-fill' : isDown ? 'bi-caret-down-fill' : null;
  const text = !hasPrevious ? (current > 0 ? 'Nuevo' : 'Sin datos') : `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;

  return (
    <div className="mt-2 pt-2" style={{ borderTop: '1px solid var(--mm-line)' }}>
      <div className="d-flex justify-content-between align-items-center">
        <span className="text-muted small">Este mes vs. {label}</span>
        <span className="d-flex align-items-center" style={{ gap: 4, color, fontWeight: 600, fontSize: '0.9rem' }}>
          {icon && <i className={`bi ${icon}`} />}
          {text}
        </span>
      </div>
      {hasPrevious && (
        <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: 2 }}>
          {format(current)} vs. {format(previous)}
        </div>
      )}
    </div>
  );
};

// Alquileres: rental invoices to generate (overdue / today / next 7 and 30 days — everyone) and, for admins, the
// projected rental income before ITBIS. Same numbers as Alquileres → Próximas facturas / Proyección.
const RentalOutlookCard = ({ outlook, navigate }) => {
  if (!outlook) return null;
  const line = (label, v, color, big) => (
    <div className="d-flex justify-content-between align-items-baseline" style={{ marginTop: 4 }}>
      <span className="text-muted small">{label}</span>
      <span style={{ fontFamily: HEADING_FONT, fontWeight: big ? 600 : 500, fontSize: big ? '1.2rem' : '1rem', color }}>
        {money(v.total)} <span className="text-muted small" style={{ fontWeight: 400 }}>· {v.count}</span>
      </span>
    </div>
  );
  const p = outlook.projection;
  return (
    <Card className="shadow-sm p-3 card-equal-height">
      <Card.Title className="d-flex justify-content-between align-items-center">
        <span>Alquileres</span>
        <span className="small" style={{ fontWeight: 400 }}>
          <a href="/rentals?tab=schedule" onClick={(e) => { e.preventDefault(); navigate('/rentals?tab=schedule'); }}>Próximas facturas →</a>
        </span>
      </Card.Title>
      <div className="text-muted small">Facturas de alquiler por generar (con ITBIS · cantidad)</div>
      {line('Vencidas sin facturar', outlook.overdue, outlook.overdue.count ? 'var(--mm-red)' : undefined, true)}
      {line('Hoy', outlook.today, 'var(--mm-yellow)')}
      {line('Próximos 7 días', outlook.week)}
      {line('Próximos 30 días', outlook.month)}
      {p && (
        <div className="mt-3 pt-2" style={{ borderTop: '1px solid var(--mm-line)' }}>
          <div className="d-flex justify-content-between align-items-center">
            <span className="text-muted small">Ingreso proyectado (sin ITBIS)</span>
            <a className="small" href="/rentals?tab=projection" onClick={(e) => { e.preventDefault(); navigate('/rentals?tab=projection'); }}>Proyección →</a>
          </div>
          <Row className="mt-2 g-2 text-center">
            {[[`${monthLabel(p.thisMonth.month)}`, p.thisMonth.subtotal, 'var(--mm-cyan)'], [`${monthLabel(p.nextMonth.month)}`, p.nextMonth.subtotal], ['Recurrente mensual', p.monthlyRunRate, 'var(--mm-green)']].map(([label, v, color]) => (
              <Col xs={4} key={label}>
                <div className="text-muted" style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>{label}</div>
                <div style={{ fontFamily: HEADING_FONT, fontWeight: 600, fontSize: '1.05rem', color }}>{money(v)}</div>
              </Col>
            ))}
          </Row>
          <div className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>{p.active} {p.active === 1 ? 'alquiler activo' : 'alquileres activos'} · supone que siguen hasta su fecha de fin</div>
        </div>
      )}
    </Card>
  );
};

// A dashboard card: a title and its figure(s), optionally a link to the page behind it.
const StatCard = ({ title, to, navigate, children }) => (
  <Card className={`shadow-sm p-3 dashboard-card${to ? ' dashboard-card-link' : ''}`} style={{ justifyContent: 'flex-start' }} onClick={to ? () => navigate(to) : undefined}>
    <div className="text-muted small">{title}</div>
    {children}
  </Card>
);

// Slice colors for the "Gastos del mes" pie (the theme's accents first, the folded "Demás categorías" in grey).
const UNITS_SHOWN = 12; // kinds of equipment drawn in the rental-services chart

// The rental-type tiles, each in its own color (the same order as the Alquileres page's cards).
const TYPE_COLORS = ['var(--mm-cyan)', 'var(--mm-violet)', 'var(--mm-teal)', 'var(--mm-orange)', 'var(--mm-pink)', 'var(--mm-blue)', 'var(--mm-yellow)', 'var(--mm-green)'];

const EXPENSE_COLORS = ['#F472B6', '#F5B700', '#A78BFA', '#FB923C', '#60A5FA', '#2DD4BF', '#9DB3C2'];

// Admins: this month's expenses as a pie by category, with the month's total at the bottom. Clicking a slice (or its
// legend entry) opens Gastos filtered to that category and month; anywhere else on the card opens the whole month.
const ExpensesCard = ({ gastos, navigate }) => {
  const slices = gastos?.byCategory || [];
  const total = gastos?.total || 0;
  const monthLink = (category) => {
    const params = new URLSearchParams({ desde: gastos?.from || '', hasta: gastos?.to || '' });
    if (category) params.set('categoria', category);
    return `/expenses?${params}`;
  };
  const openSlice = (index) => {
    const slice = slices[index];
    navigate(monthLink(slice && !slice.rest ? slice.category : ''));
  };
  const chart = {
    labels: slices.map(s => s.category),
    datasets: [{
      data: slices.map(s => s.total),
      backgroundColor: slices.map((s, i) => (s.rest ? EXPENSE_COLORS[6] : EXPENSE_COLORS[i % 6])),
      borderColor: '#12293B',
      borderWidth: 2,
    }],
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '55%',
    onClick: (evt, elements) => openSlice(elements.length ? elements[0].index : -1),
    onHover: (evt, elements) => { evt.native.target.style.cursor = elements.length ? 'pointer' : 'default'; },
    plugins: {
      // Our own list beside the donut (below): Chart.js' legend wraps into a second column when the card is short.
      legend: { display: false },
      tooltip: { callbacks: { label: (c) => `${c.label}: ${money(c.parsed)} (${total ? Math.round((c.parsed / total) * 100) : 0}%)` } },
    },
  };
  return (
    <StatCard title="Gastos del mes por categoría" to={monthLink('')} navigate={navigate}>
      {slices.length === 0 ? (
        <div className="text-center text-muted small flex-grow-1 d-flex align-items-center justify-content-center">Sin gastos registrados este mes.</div>
      ) : (
        // The chart handles its own clicks (a slice → that category); stop them reaching the card's link.
        <div className="mt-2 d-flex align-items-center gap-3" style={{ height: 140 }} onClick={(e) => e.stopPropagation()}>
          <div style={{ width: 140, height: 140, flexShrink: 0 }}><Doughnut data={chart} options={options} /></div>
          <ul className="list-unstyled mb-0 small flex-grow-1" style={{ minWidth: 0, color: '#9DB3C2' }}>
            {slices.map((s, i) => (
              <li key={s.category} className="d-flex align-items-center gap-2" style={{ cursor: 'pointer', lineHeight: 1.6 }} onClick={() => openSlice(i)}>
                <span style={{ width: 10, height: 10, flexShrink: 0, background: chart.datasets[0].backgroundColor[i] }} />
                <span className="text-truncate" title={s.category}>{s.category}</span>
                <span className="ms-auto text-nowrap">{money(s.total)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="mt-auto pt-2 d-flex justify-content-between align-items-baseline" style={{ borderTop: '1px solid var(--mm-line)' }}>
        <span className="text-muted small">Gastos del mes</span>
        <span style={{ fontFamily: HEADING_FONT, fontWeight: 600, fontSize: '1.35rem', color: 'var(--mm-pink)' }}>{money(total)}</span>
      </div>
    </StatCard>
  );
};

const SalesDashboard = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    API.get('/dashboard')
      .then(res => setData(res.data))
      .catch(err => {
        console.error('Error fetching dashboard snapshot:', err);
        setError('No se pudo cargar el panel de ventas.');
      });
  }, []);

  if (error) {
    return <div className="container mt-5"><div className="alert alert-danger">{error}</div></div>;
  }

  if (!data) {
    return <Loading page label="Cargando panel de ventas..." />;
  }

  const dailyChart = {
    labels: data.dailySalesVsQuotes.map(d => d.day),
    datasets: [
      { label: 'Ventas', data: data.dailySalesVsQuotes.map(d => d.ventas), backgroundColor: 'rgba(92,200,245,0.7)', borderRadius: 4 },
      { label: 'Cotizaciones', data: data.dailySalesVsQuotes.map(d => d.cotizaciones), backgroundColor: 'rgba(157,179,194,0.6)', borderRadius: 4 },
    ],
  };

  // Sales staff see two charts side by side (sales vs quotes, and income receipts) sized to fit the
  // screen without scrolling; admins keep the original layout with the year-over-year chart.
  const incomeChart = {
    labels: (data.dailyIncome || []).map(d => d.day),
    datasets: [{ label: 'Recibos de ingresos', data: (data.dailyIncome || []).map(d => d.recibos), backgroundColor: 'rgba(74,222,128,0.65)', borderRadius: 4 }],
  };
  const fitToScreen = (chart) => (
    <div style={{ height: 'clamp(260px, 42vh, 420px)' }}>{chart}</div>
  );
  const sideBySide = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } };

  // Company-wide totals, the year-over-year chart and the rankings only come from the server for admins.
  const showCompany = Array.isArray(data.monthlyComparison);

  // Admins: equipment out on active rentals, by kind (Camas, Sillas de ruedas…; utils/equipmentLabel.js) — the same
  // grouping as the Alquileres page's cards. A rental with two lines of one kind counts once.
  const rentalTypes = Object.values((data.activeRentalLines || []).reduce((acc, l) => {
    const kind = equipmentKind(l.description);
    const g = acc[kind] || (acc[kind] = { kind, rentals: new Set(), units: 0 });
    l.rentals.forEach(id => g.rentals.add(id));
    g.units += l.units;
    return acc;
  }, {})).map(g => ({ ...g, rentals: g.rentals.size })).sort((a, b) => b.rentals - a.rentals || a.kind.localeCompare(b.kind));

  // Admins: services sold this month per product (only what sold), as a radar ("web") chart: its shape shows the mix at a
  // glance — one long spike where sales are concentrated. The spokes are on a COMPRESSED scale (log10 of units + 1): on a
  // true scale one product at ~98% leaves every other point at the center and the web collapses to a line. The real
  // units and shares are in the tooltips and the line under the chart. A radar needs 3+ axes; with fewer it's bars.
  // Rental services by kind — Camas, Sillas de ruedas, Concentradores… (utils/equipmentLabel.js) — most sold first, the
  // first UNITS_SHOWN drawn.
  const unitsKinds = Object.values((data.unitsSoldMonth?.items || []).reduce((acc, u) => {
    const kind = equipmentKind(u.name);
    acc[kind] = { name: kind, quantity: (acc[kind]?.quantity || 0) + u.quantity };
    return acc;
  }, {})).sort((a, b) => b.quantity - a.quantity || a.name.localeCompare(b.name));
  const unitsItems = unitsKinds.slice(0, UNITS_SHOWN);
  const unitsTotal = unitsItems.reduce((sum, u) => sum + u.quantity, 0);
  const shortName = (name) => (name.length > 18 ? `${name.slice(0, 17)}…` : name);
  const unitsRadar = unitsItems.length >= 3;
  const unitsRadarChart = {
    labels: unitsItems.map(u => shortName(u.name)),
    datasets: [{
      label: 'Alquileres vendidos', data: unitsItems.map(u => Math.log10(u.quantity + 1)),
      backgroundColor: 'rgba(245,183,0,0.25)', borderColor: 'rgba(245,183,0,0.9)', borderWidth: 2,
      pointBackgroundColor: 'rgba(245,183,0,1)', pointRadius: 3, fill: true,
    }],
  };
  const unitsRadarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: { callbacks: {
        title: (items) => unitsItems[items[0].dataIndex]?.name,
        label: (c) => {
          const q = unitsItems[c.dataIndex]?.quantity || 0;
          return `${q.toLocaleString('es-DO')} unidades (${unitsTotal ? Math.round((q / unitsTotal) * 100) : 0}%)`;
        },
      } },
    },
    scales: {
      r: {
        beginAtZero: true,
        angleLines: { color: 'rgba(157,179,194,0.25)' },
        grid: { color: 'rgba(157,179,194,0.2)' },
        pointLabels: { color: '#9DB3C2', font: { size: 11 } },
        ticks: { display: false },
      },
    },
  };
  const unitsChart = {
    labels: unitsItems.map(u => u.name),
    datasets: [{ label: 'Alquileres vendidos', data: unitsItems.map(u => u.quantity), backgroundColor: 'rgba(245,183,0,0.65)', borderRadius: 4 }],
  };
  const unitsOptions = {
    indexAxis: 'y',
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => `${c.parsed.x} unidades` } } },
    scales: {
      x: { beginAtZero: true },
      y: { ticks: { callback(value) { const label = this.getLabelForValue(value); return label.length > 24 ? `${label.slice(0, 23)}…` : label; } } },
    },
  };

  const yearlyChart = !showCompany ? null : {
    labels: MONTH_LABELS,
    datasets: [
      {
        label: String(data.currentYear),
        data: data.monthlyComparison.map(m => m.currentYear),
        borderColor: '#5CC8F5',
        backgroundColor: '#5CC8F5',
        tension: 0.3,
      },
      {
        label: String(data.lastYear),
        data: data.monthlyComparison.map(m => m.lastYear),
        borderColor: '#9DB3C2', // the comparison series is grey: red next to green reads as Christmas
        backgroundColor: '#9DB3C2',
        tension: 0.3,
      },
    ],
  };

  const hasYearlyData = showCompany && (data.hasCurrentYearData || data.hasLastYearData);

  return (
    <div className="container-fluid p-4">
      <h2 className="app-title mb-3" style={{ textAlign: 'left' }}>Panel de Ventas</h2>
      <DailyBrief />

      <Row className="mb-3">
        {showCompany ? (
          <>
            <Col md={6} xl={3} className="mb-3 mb-xl-0">
              <StatCard title="Ventas" to="/invoices" navigate={navigate}>
                <PeriodRows values={data.periods.ventas} format={money} color="var(--mm-cyan)" rows={PERIOD_ROWS_YEAR} />
                <YoyComparison current={data.yoyComparison.ventas.current} previous={data.yoyComparison.ventas.previous} month={data.currentMonth} year={data.lastYear} format={money} />
              </StatCard>
            </Col>
            <Col md={6} xl={3} className="mb-3 mb-xl-0">
              <StatCard title="Ingresos recibidos" to="/ingresos?tab=history" navigate={navigate}>
                <PeriodRows values={data.periods.ingresos} format={money} color="var(--mm-green)" rows={PERIOD_ROWS_YEAR} />
                <YoyComparison current={data.yoyComparison.ingresos.current} previous={data.yoyComparison.ingresos.previous} month={data.currentMonth} year={data.lastYear} format={money} />
              </StatCard>
            </Col>
            <Col md={6} xl={3} className="mb-3 mb-xl-0">
              <StatCard title="Cuentas por cobrar" to="/reports?tab=cobros" navigate={navigate}>
                <h2 className="mt-2 mb-1" style={{ color: 'var(--mm-yellow)' }}>{money(data.periods.porCobrar.total)}</h2>
                <div className="small text-muted">Pendiente de lo facturado:</div>
                <PeriodRows values={data.periods.porCobrar} format={money} color="var(--mm-yellow)" />
              </StatCard>
            </Col>
            <Col md={6} xl={3} className="mb-3 mb-xl-0">
              <ExpensesCard gastos={data.gastosMes} navigate={navigate} />
            </Col>
          </>
        ) : (
          <>
            <Col md={6} xl={3} className="mb-3 mb-xl-0">
              <StatCard title="Tus ventas de hoy" to="/invoices" navigate={navigate}>
                <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-cyan)' }}>{money(data.ventasHoy)}</h2>
              </StatCard>
            </Col>
            <Col md={6} xl={3} className="mb-3 mb-xl-0">
              <StatCard title="Facturado por ti esta semana" to="/invoices" navigate={navigate}>
                <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-cyan)' }}>{money(data.miFacturacionSemana?.total)}</h2>
                <div className="small text-muted mt-1">
                  {data.miFacturacionSemana?.count || 0} {data.miFacturacionSemana?.count === 1 ? 'factura' : 'facturas'} · de lunes a domingo
                </div>
              </StatCard>
            </Col>
            <Col md={6} xl={3} className="mb-3 mb-xl-0">
              <StatCard title="Cobrado por ti hoy" to="/ingresos?tab=history" navigate={navigate}>
                <h2 className="mt-2 mb-0" style={{ color: 'var(--mm-green)' }}>{money(data.ingresosHoy)}</h2>
                <div className="small text-muted mt-1">{data.cobrosHoy || 0} {data.cobrosHoy === 1 ? 'cobro verificado' : 'cobros verificados'} · registrados por ti</div>
              </StatCard>
            </Col>
            <Col md={6} xl={3} className="mb-3 mb-xl-0">
              <StatCard title="Tus cotizaciones de hoy" to="/quotes" navigate={navigate}>
                <h2 className="mt-2 mb-0">{data.cotizacionesHoy}</h2>
              </StatCard>
            </Col>
          </>
        )}
      </Row>

      <Row className="mb-3">
        <Col md={6} xl={showCompany ? 4 : 6} className={showCompany ? 'mb-3 mb-xl-0' : undefined}>
          <Card className="shadow-sm p-3 card-equal-height">
            <Card.Title>{showCompany ? 'Ventas vs Cotizaciones por Día (mes actual)' : 'Tus ventas vs cotizaciones por día (mes actual)'}</Card.Title>
            {showCompany
              ? <Bar data={dailyChart} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
              : fitToScreen(<Bar data={dailyChart} options={sideBySide} />)}
          </Card>
        </Col>
        {!showCompany && (
          <Col md={6}>
            <Card className="shadow-sm p-3 card-equal-height">
              <Card.Title>Lo que cobraste por día (mes actual)</Card.Title>
              {fitToScreen(
                <Bar
                  data={incomeChart}
                  options={{ ...sideBySide, plugins: { ...sideBySide.plugins, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${money(c.parsed.y)}` } } } }}
                />,
              )}
            </Card>
          </Col>
        )}
        {showCompany && (
        <Col md={6} xl={4} className="mb-3 mb-xl-0">
          <Card className="shadow-sm p-3 card-equal-height">
            <Card.Title>Ventas por Mes — {data.currentYear} vs {data.lastYear}</Card.Title>
            {hasYearlyData ? (
              <Line data={yearlyChart} options={{ responsive: true, plugins: { legend: { position: 'bottom' } } }} />
            ) : (
              <div className="text-center py-5">
                <i className="bi bi-graph-up-arrow pulse-glow" style={{ fontSize: '3rem', color: 'var(--mm-cyan)' }} />
                <div className="mt-3" style={{ color: 'var(--mm-muted)', fontFamily: "'DM Sans', sans-serif", fontSize: '1.1rem' }}>
                  Todavía no hay suficiente historial para comparar años.
                </div>
                <div className="text-muted small mt-1">Esta gráfica despertará en cuanto empieces a facturar.</div>
              </div>
            )}
          </Card>
        </Col>
        )}
        {showCompany && (
        <Col md={12} xl={4}>
          <Card className="shadow-sm p-3 card-equal-height">
            <Card.Title>Alquileres vendidos por servicio (mes actual)</Card.Title>
            {unitsItems.length === 0 ? (
              <div className="text-center py-5">
                <i className="bi bi-box-seam" style={{ fontSize: '3rem', color: 'var(--mm-yellow)' }} />
                <div className="mt-3" style={{ color: 'var(--mm-muted)', fontFamily: HEADING_FONT, fontSize: '1.1rem' }}>Todavía no se han facturado alquileres este mes.</div>
              </div>
            ) : (
              <>
                {unitsRadar ? (
                  <div style={{ height: 300 }}>
                    <Radar data={unitsRadarChart} options={unitsRadarOptions} />
                  </div>
                ) : (
                  <div style={{ height: Math.max(220, unitsItems.length * 30 + 60) }}>
                    <Bar data={unitsChart} options={unitsOptions} />
                  </div>
                )}
                {unitsTotal > 0 && (
                  <div className="small text-muted mt-2">
                    {unitsItems.slice(0, 3).map(u => `${u.name} ${Math.round((u.quantity / unitsTotal) * 100)}%`).join(' · ')}
                    {unitsRadar && <div style={{ fontSize: '0.75rem' }}>Escala comprimida para que se vean los servicios pequeños; los % son reales.</div>}
                  </div>
                )}
                {unitsKinds.length > unitsItems.length && (
                  <div className="small text-muted mt-2">Se muestran los {unitsItems.length} más vendidos de {unitsKinds.length} tipos de equipo alquilados.</div>
                )}
              </>
            )}
          </Card>
        </Col>
        )}
      </Row>

      <Row className="mb-3">
        <Col lg={6} className="mb-3 mb-lg-0">
          <RentalOutlookCard outlook={data.rentalOutlook} navigate={navigate} />
        </Col>
        <Col lg={6}>
          <Card className="shadow-sm p-3 card-equal-height">
            <Card.Title>Inventario Disponible</Card.Title>
            {!data.inventory?.length ? (
              <div className="text-muted">Sin productos con inventario rastreado — actívalo desde Productos.</div>
            ) : (
              <table className="table table-sm mb-0">
                <thead>
                  <tr>
                    <th>Categoría</th>
                    <th className="text-end">Stock Total</th>
                    <th className="text-end">Alquilado</th>
                    <th className="text-end">Disponible</th>
                  </tr>
                </thead>
                <tbody>
                  {data.inventory.map((i, idx) => (
                    <tr key={idx}>
                      <td>{i.category}</td>
                      <td className="text-end">{i.stockTotal}</td>
                      <td className="text-end">{i.rentedOut}</td>
                      <td className="text-end" style={{ color: i.available <= 0 ? 'var(--mm-red)' : undefined, fontWeight: 600 }}>
                        {i.available}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </Col>
      </Row>

      {showCompany && (
      <Row>
        <Col md={6}>
          <Card className="shadow-sm p-3 card-equal-height">
            <Card.Title>Top 5 Clientes ({data.currentYear})</Card.Title>
            {data.topClients.length === 0 ? (
              <div className="text-muted">Sin datos todavía.</div>
            ) : (
              <table className="table table-sm mb-0">
                <thead>
                  <tr><th>Cliente</th><th className="text-end">Total anual</th></tr>
                </thead>
                <tbody>
                  {data.topClients.map((c, i) => (
                    <tr key={i}>
                      <td>{c.name}</td>
                      <td className="text-end">{money(c.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm p-3 card-equal-height">
            <Card.Title>Alquileres activos por tipo</Card.Title>
            {rentalTypes.length === 0 ? (
              <div className="text-muted">No hay alquileres activos.</div>
            ) : (
              <Row className="mt-1">
                {rentalTypes.map((t, i) => {
                  const color = TYPE_COLORS[i % TYPE_COLORS.length];
                  return (
                    <Col key={t.kind} xs={6} lg={4} className="mb-2">
                      <div role="button" className="dashboard-card-link h-100" title={`${t.kind} — ver estos alquileres`}
                        onClick={() => navigate(`/rentals?equipo=${encodeURIComponent(t.kind)}`)}
                        style={{ background: 'var(--mm-panel-2)', border: '1px solid var(--mm-line)', borderTop: `3px solid ${color}`, borderRadius: 10, padding: '8px 12px' }}>
                        <div className="small text-truncate" style={{ color: 'var(--mm-muted)' }}>{t.kind}</div>
                        <div className="d-flex align-items-baseline gap-2">
                          <span style={{ fontSize: '1.5rem', fontWeight: 700, color }}>{t.rentals}</span>
                          <span className="small text-muted">{t.rentals === 1 ? 'alquiler' : 'alquileres'}</span>
                        </div>
                        {t.units !== t.rentals && <div className="small text-muted">{t.units} unidades</div>}
                      </div>
                    </Col>
                  );
                })}
              </Row>
            )}
            <div className="small text-muted mt-1">
              {rentalTypes.reduce((s2, t) => s2 + t.rentals, 0) > 0 && 'Toca un tipo para ver sus alquileres.'}
            </div>
          </Card>
        </Col>
      </Row>
      )}
    </div>
  );
};

export default SalesDashboard;
